<script>
export default {
    name: 'ProviderManagementFilters',

    data() {
        return {
            localSearch: '',
            searchDebounceTimer: null,
        };
    },

    computed: {
        filters() {
            return this.$store.getters.ADMIN_PUBLISHERS_FILTERS;
        },

        statusOptions() {
            return [
                { label: 'All Statuses', value: null },
                { label: 'Active', value: 'active' },
                { label: 'Deleted', value: 'deleted' },
                { label: 'Migrated', value: 'migrated' },
            ];
        },
    },

    mounted() {
        // Sync local search from store on mount (e.g. after navigation back)
        this.localSearch = this.$store.getters.ADMIN_PUBLISHERS_FILTERS?.search || '';
    },

    methods: {
        // ── Search (debounced) ───────────────────────────────────────────────
        handleSearchInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.applySearchFilter();
            }, 300);
        },

        applySearchFilter() {
            const val = this.localSearch.trim() || null;
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderManagementFilters.applySearchFilter] applying search filter'
                );
            }
            this.$store.commit('SET_ADMIN_PUBLISHERS_FILTERS', { search: val });
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
            this.$store.dispatch('loadAdminPublishers');
        },

        clearSearch() {
            this.localSearch = '';
            this.applySearchFilter();
        },

        // ── Status dropdown ──────────────────────────────────────────────────
        handleStatusChange(event) {
            const value = event.target.value === '' ? null : event.target.value;
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[ProviderManagementFilters.handleStatusChange] status →',
                    value
                );
            }
            this.$store.commit('SET_ADMIN_PUBLISHERS_FILTERS', {
                status: value,
            });
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
            this.$store.dispatch('loadAdminPublishers');
        },

        // ── Clear all ────────────────────────────────────────────────────────
        clearAllFilters() {
            this.localSearch = '';
            clearTimeout(this.searchDebounceTimer);
            this.$store.commit('SET_ADMIN_PUBLISHERS_FILTERS', {
                search: null,
                status: null,
            });
            this.$store.commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
            this.$store.dispatch('loadAdminPublishers');
        },
    },
};
</script>

<template>
    <div class="filters_container">
        <!-- Search input -->
        <div class="filter_group">
            <label class="filter_label">Search</label>
            <div class="search_box">
                <input
                    v-model="localSearch"
                    @input="handleSearchInput"
                    type="text"
                    placeholder="Search by name or email..."
                    class="filter_input"
                />
                <button
                    v-if="localSearch"
                    @click="clearSearch"
                    class="clear_btn"
                    title="Clear"
                >
                    ✕
                </button>
            </div>
        </div>

        <!-- Status dropdown -->
        <div class="filter_group">
            <label class="filter_label">Status</label>
            <select
                :value="filters.status || ''"
                @change="handleStatusChange"
                class="filter_select"
            >
                <option
                    v-for="opt in statusOptions"
                    :key="String(opt.value)"
                    :value="opt.value === null ? '' : opt.value"
                >
                    {{ opt.label }}
                </option>
            </select>
        </div>

        <!-- Clear all button -->
        <div class="filter_group filter_group_actions">
            <button
                v-if="filters.search || filters.status"
                @click="clearAllFilters"
                class="btn_clear_all"
            >
                Clear All
            </button>
        </div>
    </div>
</template>

<style scoped>
.filters_container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 16px;
}

.filter_group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 180px;
}

.filter_group_actions {
    flex-direction: row;
    align-items: flex-end;
    padding-bottom: 2px;
}

.filter_label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.search_box {
    position: relative;
    display: flex;
    align-items: center;
}

.filter_input {
    padding: 8px 36px 8px 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    width: 240px;
    transition: border-color 0.2s;
}

.filter_input:focus {
    outline: none;
    border-color: var(--primary-color);
}

.clear_btn {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: var(--text-color-secondary);
    cursor: pointer;
    font-size: 14px;
    padding: 4px;
    line-height: 1;
}

.clear_btn:hover {
    color: var(--text-color);
}

.filter_select {
    padding: 8px 10px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    cursor: pointer;
    min-width: 160px;
}

.filter_select:focus {
    outline: none;
    border-color: var(--primary-color);
}

.btn_clear_all {
    padding: 8px 14px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: transparent;
    color: var(--text-color-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn_clear_all:hover {
    border-color: var(--text-color);
    color: var(--text-color);
}
</style>
